import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  InviteCreatedBanner,
  InviteForm,
  PendingInvitesList,
  AdminRow,
} from "./admin-team/AdminTeamParts";

/**
 * Owner-only Team & Invites panel.
 *
 * Slim orchestrator — owns ALL state + API calls + handlers, but renders
 * via four extracted view components (see ./admin-team/AdminTeamParts.jsx):
 *
 *   • <InviteCreatedBanner/>   confirmation after creating an invite
 *   • <InviteForm/>            "New invite" form (toggle + perm checkboxes)
 *   • <PendingInvitesList/>    table of pending invitations
 *   • <AdminRow/>              one row per existing admin, inline-editable
 *
 * Splitting this file kept the same data-testids (so all existing tests +
 * the testing agent still pass) and brought the parent from ~440 lines down
 * to a focused ~150 lines that's easy to scan for "what API does what".
 */
export default function AdminTeamPanel() {
  const { adminPerms } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", permissions: [] });
  const [createdInvite, setCreatedInvite] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(null);  // user_id of admin being edited
  const [editPerms, setEditPerms] = useState([]);
  const [err, setErr] = useState("");

  const grantable = useMemo(
    () => adminPerms?.all_grantable_permissions || [],
    [adminPerms],
  );

  const load = async () => {
    try {
      const [a, i] = await Promise.all([
        api.get("/admin/team"),
        api.get("/admin/invites"),
      ]);
      setAdmins(Array.isArray(a.data) ? a.data : []);
      setInvites(Array.isArray(i.data) ? i.data : []);
    } catch (_e) {
      // Non-owners get 403 — surface nothing, the panel is hidden anyway.
    }
  };
  // `load` is stable; initial admin+invite fetch fires once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // ─── Form helpers ────────────────────────────────────────────────
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const togglePerm = (perm) =>
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(perm)
        ? f.permissions.filter((p) => p !== perm)
        : [...f.permissions, perm],
    }));

  // ─── Invite lifecycle ────────────────────────────────────────────
  const createInvite = async () => {
    setErr("");
    if (!form.email || !form.name) {
      setErr("Name and email are required.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invites", form);
      setCreatedInvite(data);
      setForm({ email: "", name: "", permissions: [] });
      setShowForm(false);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not create invitation.");
    }
    setBusy(false);
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this invite? The link will stop working.")) return;
    try {
      await api.delete(`/admin/invites/${id}`);
      await load();
    } catch (e) {
      console.error("Failed to revoke invite", e);
    }
  };

  const copyLink = (url) => {
    const full = url.startsWith("http") ? url : `${window.location.origin}${url}`;
    navigator.clipboard?.writeText(full);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  // ─── Admin row editing ───────────────────────────────────────────
  const startEdit = (a) => {
    setEditing(a.user_id);
    setEditPerms(a.permissions || []);
  };
  const cancelEdit = () => setEditing(null);
  const togglePermEdit = (perm) =>
    setEditPerms((p) => (p.includes(perm) ? p.filter((x) => x !== perm) : [...p, perm]));

  const saveEdit = async (a) => {
    try {
      await api.patch(`/admin/team/${a.user_id}`, { permissions: editPerms });
      setEditing(null);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Update failed.");
    }
  };

  const forcePw = async (a) => {
    if (!window.confirm(`Force ${a.name || a.email} to rotate their password on next login?`)) return;
    try {
      await api.post(`/admin/team/${a.user_id}/force-password-change`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Action failed.");
    }
  };

  const revokeAdmin = async (a) => {
    if (!window.confirm(`Revoke admin access for ${a.name || a.email}? They will become a customer.`)) return;
    try {
      await api.delete(`/admin/team/${a.user_id}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Revoke failed.");
    }
  };

  // ─── Render ──────────────────────────────────────────────────────
  return (
    <div data-testid="admin-team-panel" className="space-y-8">
      {createdInvite && (
        <InviteCreatedBanner
          invite={createdInvite}
          onCopy={copyLink}
          copied={copied}
          onDismiss={() => setCreatedInvite(null)}
        />
      )}

      <InviteForm
        showForm={showForm}
        onToggleForm={() => setShowForm((s) => !s)}
        form={form}
        onSetField={setField}
        onTogglePerm={togglePerm}
        grantable={grantable}
        busy={busy}
        err={err}
        onCreate={createInvite}
      />

      <PendingInvitesList
        invites={invites}
        onCopy={copyLink}
        onRevoke={revokeInvite}
      />

      <div>
        <div className="text-[10px] uppercase tracking-widest text-[#064E3B] font-semibold">Admin team</div>
        <div className="space-y-2 mt-3">
          {admins.length === 0 && (
            <div className="p-8 text-center text-[#071A2D]/60 rounded-2xl bg-[#FFF8EE] border border-dashed border-[#071A2D]/15">
              No admins yet — invite one above.
            </div>
          )}
          {admins.map((a) => {
            const isOwnerRow = a.role_tier === "owner" || a.founder || a.is_protected;
            return (
              <AdminRow
                key={a.user_id}
                admin={a}
                isOwnerRow={isOwnerRow}
                isEditing={editing === a.user_id}
                editPerms={editPerms}
                grantable={grantable}
                onStartEdit={startEdit}
                onTogglePermEdit={togglePermEdit}
                onSaveEdit={saveEdit}
                onCancelEdit={cancelEdit}
                onForcePw={forcePw}
                onRevoke={revokeAdmin}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
